import { Component, Injector, Input, OnChanges, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { BodyComponentObject, CategoryTemplateMessage, StatusTemplateMessage, TemplateMessageObject, TemplateMessageObjectPager } from "@trendbuild/trend-cloud-api";
import { AbstractComponent, Channel, ChannelService, ConfirmationComponent, Pager, WaTemplateRead } from "lib-trend-core";
import { debounceTime, takeUntil } from "rxjs";
import { isTemplateEditable } from "../../template-builder/template-editability";

@Component({
    selector: 'settings-template-list-component',
    // templateUrl: './settings-template-list.component.html',
    templateUrl: './settings-template-list.v2.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SettingsTemplateList extends AbstractComponent implements OnInit, OnChanges {

  @Input() channel: Channel

  @Input() showHeading: boolean = true;

  private loadedChannelId: string | null = null;

  private loadSeq: number = 0;

  selectedTemplate: TemplateMessageObject | null = null;
  templates: Array<TemplateMessageObject> = new Array<TemplateMessageObject>()
  allTemplates: TemplateMessageObject[] = [];

  /**
   * The provider returns every template in one call, so the list is paged in memory rather than by
   * the API — the paginator contract is the same one the other lists use.
   */
  pager: Pager<TemplateMessageObject> = new Pager<TemplateMessageObject>({ perPage: 10 });
  form: FormGroup;
  searchControl: FormControl = new FormControl('');
  selectedCategory: string = '';
  selectedStatus: string = '';

  categories = [
    { value: 'MARKETING', label: 'channel.settingsTemplateList.categoryMarketing' },
    { value: 'UTILITY', label: 'channel.settingsTemplateList.categoryUtility' },
    { value: 'AUTHENTICATION', label: 'channel.settingsTemplateList.categoryAuthentication' },
  ];

  /**
   * Label of every status the Graph API can return (RF-53). Used to render the badge, so a template
   * in any state is readable — the previous code returned an empty string for anything outside the
   * first five, which rendered a blank badge.
   */
  private readonly statusLabels: { [status: string]: string } = {
    APPROVED: 'channel.settingsTemplateList.statusApproved',
    PENDING: 'channel.settingsTemplateList.statusPending',
    IN_APPEAL: 'channel.settingsTemplateList.statusInAppeal',
    REJECTED: 'channel.settingsTemplateList.statusRejected',
    PAUSED: 'channel.settingsTemplateList.statusPaused',
    DISABLED: 'channel.settingsTemplateList.statusDisabled',
    LIMIT_EXCEEDED: 'channel.settingsTemplateList.statusLimitExceeded',
    ARCHIVED: 'channel.settingsTemplateList.statusArchived',
    PENDING_DELETION: 'channel.settingsTemplateList.statusPendingDeletion',
    DELETED: 'channel.settingsTemplateList.statusDeleted',
  };

  /**
   * The filter offers exactly the enum Meta documents for the `status` query parameter of
   * `GET /<WABA_ID>/message_templates`. Every one of them is answered by the provider, including
   * ARCHIVED — which is why choosing a status refetches instead of filtering the list in memory:
   * the unfiltered listing never contains archived templates, so no local filter could find them.
   */
  statuses = ['APPROVED', 'IN_APPEAL', 'PENDING', 'REJECTED', 'PENDING_DELETION',
    'DELETED', 'DISABLED', 'PAUSED', 'LIMIT_EXCEEDED', 'ARCHIVED']
    .map((value) => ({ value, label: this.statusLabels[value] }));

  constructor(
    injector: Injector,
    private channelService: ChannelService,
    private readonly dialog: MatDialog,
  ) {
    super(injector);
    this.form = this.formBuilder.group({});
    this.setupSearchListener();
  }

  ngOnInit(): void {
    this.loadTemplatesForBoundChannel();
  }

  /**
   * The channel can arrive after creation and can be swapped without the component being destroyed
   * — the campaign page keeps one instance and changes which channel it points at. Both entry
   * points funnel through the same guarded load, so neither fetches twice.
   */
  ngOnChanges(): void {
    this.loadTemplatesForBoundChannel();
  }

  private loadTemplatesForBoundChannel(): void {
    const idChannel = this.channel?._id;

    // No channel bound: there is nothing to show and nothing to wait for. Retiring the load token
    // stops an answer still in flight from landing on a channel that is gone, and the skeleton has
    // to be cleared here — that answer is the only thing that would have cleared it, and
    // `loadedChannelId` is reset so re-binding the same channel loads again instead of being
    // mistaken for a channel already listed.
    if (!idChannel) {
      this.loadSeq++;
      this.loadedChannelId = null;
      this.loading = false;
      this.loadingContent = false;
      return;
    }

    if (idChannel === this.loadedChannelId) {
      return;
    }
    this.loadedChannelId = idChannel;

    // Filters describe the previous channel's list; carrying the status over would also make the
    // first request for the new channel a filtered one, caching a partial listing under it.
    this.selectedStatus = '';
    this.selectedCategory = '';
    this.searchControl.setValue('', { emitEvent: false });

    this.listWaTemplates();
  }

  async listWaTemplates() {
    if (!this.channel?._id) {
      return;
    }
    this.loading = true;
    this.loadingContent = true;

    // An answer already read once is replayed synchronously while an answer still being fetched is
    // not, so a load started later can land first — and then the earlier one would overwrite it.
    // That is not cosmetic: `this.channel` and the rows would disagree, and the row actions send
    // `this.channel._id` together with the row's template id, so Excluir would ask one WhatsApp
    // account to delete another account's template.
    const seq = ++this.loadSeq;
    const isCurrentLoad = () => seq === this.loadSeq;

    this.channelService.getTemplatesByChannel(this.channel._id, this.selectedStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (templateMessageObjectPager: TemplateMessageObjectPager) => {
          if (!isCurrentLoad()) {
            return;
          }
          try {
            const allTemplates = templateMessageObjectPager.data || [];
            this.allTemplates = allTemplates;
            this.templates = allTemplates;
            this.applyPager(1);
            this.loading = false;
          } catch (error) {
            console.error('Error fetching templates:', error);
            this.templates = [];
            this.allTemplates = [];
            this.applyPager(1);
          }
          this.loading = false;
        },
        // `complete` does not run when the stream errors, so clearing the skeleton only there left
        // a failed fetch loading forever, with nothing said.
        error: (err) => {
          if (!isCurrentLoad()) {
            return;
          }
          this.loading = false;
          this.loadingContent = false;
          this.alertService.error(err?.error?.message);
        },
        complete: () => {
          // Guarded too, or a superseded load would clear the skeleton of the one still running.
          // Safe against a stuck skeleton because every path that retires the token either starts
          // a load of its own or clears the flags itself.
          if (isCurrentLoad()) {
            this.loadingContent = false;
          }
        }
      });
  }


  private setupSearchListener() {
    this.searchControl.valueChanges
      .pipe(debounceTime(300))
      .subscribe((value: string) => {
        this.filterTemplates(value);
      });
  }

  filterByCategory(category: string) {
    this.selectedCategory = category;
    this.applyFilters();
  }

  /** Refetches: the status is answered by the provider, unlike search and category. */
  filterByStatus(status: string) {
    this.selectedStatus = status;
    this.loadingContent = true;
    this.listWaTemplates();
  }

  filterTemplates(event: any) {
    const value = typeof event === 'string' ? event : event?.target?.value || '';
    this.applyFilters(value);
  }

  private applyFilters(searchValue: string = this.searchControl.value) {
    let filteredTemplates = [...this.allTemplates];

    if (this.selectedCategory) {
      filteredTemplates = filteredTemplates.filter(template =>
        template.category === this.selectedCategory
      );
    }

    // Aplicar filtro de busca
    if (searchValue?.trim()) {
      const search = searchValue.toLowerCase();
      filteredTemplates = filteredTemplates.filter(template =>
        template.name.toLowerCase().includes(search) ||
        template.category?.toLowerCase().includes(search) ||
        (<BodyComponentObject>template.components?.find(c => c.type === 'BODY'))?.text?.toLowerCase().includes(search)
      );
    }

    this.templates = filteredTemplates;
    this.applyPager(1);
  }

  /** Slices the filtered list into the current page. */
  private applyPager(page: number): void {
    this.pager.page = page;
    this.pager.total = this.templates.length;
    const start = (page - 1) * this.pager.perPage;
    this.pager.list = this.templates.slice(start, start + this.pager.perPage);
  }

  loadPage(page: number): void {
    this.applyPager(page);
  }

  /** Flag of a language the list has an icon for; empty when there is none, so the code is shown. */
  public languageFlag(language: string): string {
    const flags: { [code: string]: string } = {
      pt_BR: 'assets/icons/br.svg',
      pt_PT: 'assets/icons/pt.svg',
      en: 'assets/icons/us.svg',
      en_US: 'assets/icons/us.svg',
    };
    return flags[language] ?? '';
  }

  public getBodyText(template: TemplateMessageObject): string {
    const bodyComponent = <BodyComponentObject>template?.components?.find(c => c.type === 'BODY');
    return bodyComponent?.text ?? '';
  }

  /**
   * Only Marketing → Standard templates can be opened in the builder: it is the only shape the form
   * can represent, so anything else would lose content on save.
   */
  public canEdit(template: TemplateMessageObject): boolean {
    return isTemplateEditable(<WaTemplateRead><unknown>template);
  }

  public isPaused(template: TemplateMessageObject): boolean {
    return String(template?.status) === 'PAUSED';
  }

  /**
   * Archiving and unarchiving are not offered anywhere on this screen because the API has no such
   * operation: Meta documents `status` as read-only on the template node, and the only writable
   * fields are category, components, message_send_ttl_seconds, parameter_format, product_set_id and
   * creative_sourcing_spec. Templates are archived automatically after 12 months without use.
   * The status is still read here, to keep actions that would be refused out of the menu.
   */
  public isArchived(template: TemplateMessageObject): boolean {
    return String(template?.status) === 'ARCHIVED';
  }

  public isBeingRemoved(template: TemplateMessageObject): boolean {
    const status = String(template?.status);
    return status === 'DELETED' || status === 'PENDING_DELETION';
  }

  /** Editing an archived or already-removed template would be refused by the provider. */
  public canEditNow(template: TemplateMessageObject): boolean {
    return this.canEdit(template) && !this.isArchived(template) && !this.isBeingRemoved(template);
  }

  // The builder is a page, not a dialog: the three-step flow needs the whole viewport and its own
  // URL so a template under construction can be reached again (SPEC-2026-08, D-01).
  public newTemplate(): void {
    this.router.navigate(['/channel/templates', this.channel._id, 'new']);
  }

  /**
   * The Payments API - Brazil template (DEV-1992). Same page, same form: only the category, the
   * display format and the button set differ, and the builder derives all three from this flag.
   */
  public newPixTemplate(): void {
    this.router.navigate(['/channel/templates', this.channel._id, 'new'], { queryParams: { format: 'pix' } });
  }

  public editTemplate(template: TemplateMessageObject): void {
    this.router.navigate(['/channel/templates', this.channel._id, 'edit', template.id]);
  }

  public removeTemplate(template: TemplateMessageObject): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, { width: '500px' });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
      if (Boolean(confirmed) === true) {
        this.channelService.deleteTemplateByChannel(this.channel._id, template.id, template.name)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.alertService.success(this.t('channel.settingsTemplateList.templateRemovedSuccess'));
              this.cleanCacheTemplates();
            },
            error: (err) => this.alertService.error(err?.error?.message),
          });
      }
    });
  }

  public unpauseTemplate(template: TemplateMessageObject): void {
    this.channelService.unpauseTemplateByChannel(this.channel._id, template.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.alertService.success(this.t('channel.settingsTemplateList.templateUnpausedSuccess'));
          this.cleanCacheTemplates();
        },
        error: (err) => this.alertService.error(err?.error?.message),
      });
  }

  public cleanCacheTemplates() {
    // Checked before the skeleton goes up: with no channel bound the request below is never made,
    // and raising it first would leave it up with nothing coming to take it down.
    if (!this.channel?._id) {
      return;
    }

    // The skeleton is driven by `loadingContent` — `loading` is set by listWaTemplates but nothing
    // in this template reads it. Without setting it here the button did its work, including a
    // provider round trip, with no sign on screen that anything was happening.
    this.loadingContent = true;

    this.channelService.cleanCacheTemplatesByChannel(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.templates = [];
          this.allTemplates = [];
          this.listWaTemplates();
        },
        error: (err) => {
          this.loadingContent = false;
          this.alertService.error(err?.error?.message);
        }
      });
  }

  translateTemplateStatus(status: StatusTemplateMessage | string): string {
    const label = this.statusLabels[status];
    // The raw value rather than an empty string: a status Meta adds later must still be readable
    // instead of rendering as a blank badge, which is what the previous default did.
    return !!label ? this.t(label) : (status ?? '');
  }

  /** Badge colour per status, so every state the provider can return is visually distinct. */
  statusBadgeClass(status: StatusTemplateMessage | string): string {
    const colours: { [key: string]: string } = {
      APPROVED: 'bg-success',
      PENDING: 'bg-warning',
      IN_APPEAL: 'bg-info',
      REJECTED: 'bg-danger',
      PAUSED: 'bg-secondary',
      DISABLED: 'bg-dark',
      LIMIT_EXCEEDED: 'bg-danger',
      ARCHIVED: 'bg-secondary',
      PENDING_DELETION: 'bg-warning',
      DELETED: 'bg-dark',
    };
    return colours[status] ?? 'bg-light text-dark';
  }

  translateTemplateCategory(type: CategoryTemplateMessage | string): string {
    switch (type) {
      case 'AUTHENTICATION':
        return this.t('channel.settingsTemplateList.categoryAuthentication');
      case 'MARKETING':
        return this.t('channel.settingsTemplateList.categoryMarketing');
      case 'UTILITY':
        return this.t('channel.settingsTemplateList.categoryUtility');
      default:
        return '';
    }
  }

}
